<template>
  <div class="spin-wheel-container" :style="containerStyle">
    <!-- Sparkles (casino effect) -->
    <div v-if="enableSparkles" class="sparkles">
      <span
        v-for="i in 12"
        :key="i"
        class="sparkle"
        :style="getSparkleStyle(i)"
      />
    </div>

    <!-- Outer glow ring -->
    <div class="wheel-glow" />

    <!-- Light bulbs around rim -->
    <div class="wheel-lights">
      <span
        v-for="i in 20"
        :key="`light-${i}`"
        class="light-bulb"
        :class="{ active: lightActive && i % 2 === lightPhase }"
        :style="getLightStyle(i, 20)"
      />
    </div>

    <!-- SVG Wheel -->
    <svg
      ref="wheelSvg"
      class="wheel-svg"
      :viewBox="`0 0 ${svgSize} ${svgSize}`"
      :style="wheelStyle"
      role="img"
      :aria-label="`Prize wheel with ${prizes.length} segments`"
    >
      <!-- Gradient definitions -->
      <defs>
        <!-- Golden border gradient -->
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ffd700" />
          <stop offset="50%" stop-color="#d4af37" />
          <stop offset="100%" stop-color="#b8860b" />
        </linearGradient>

        <!-- Segment gradients -->
        <linearGradient
          v-for="(prize, index) in prizes"
          :id="`segmentGradient-${index}`"
          :key="`grad-${index}`"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" :stop-color="lightenColor(prize.bgColor, 20)" />
          <stop offset="100%" :stop-color="prize.bgColor" />
        </linearGradient>

        <!-- Glow filter -->
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- Wheel background circle -->
      <circle
        :cx="center"
        :cy="center"
        :r="radius - borderWidth / 2"
        fill="#1a1a2e"
        :stroke="'url(#goldGradient)'"
        :stroke-width="borderWidth"
        filter="url(#glow)"
      />

      <!-- Segments -->
      <g class="segments">
        <path
          v-for="(prize, index) in prizes"
          :key="`segment-${index}`"
          :d="getSegmentPath(index)"
          :fill="`url(#segmentGradient-${index})`"
          :stroke="borderColor"
          stroke-width="1"
          class="segment"
          :aria-label="prize.name"
        />
      </g>

      <!-- Segment labels -->
      <g class="labels">
        <text
          v-for="(prize, index) in prizes"
          :key="`label-${index}`"
          :x="getTextPosition(index).x"
          :y="getTextPosition(index).y"
          :transform="
            `rotate(${getTextPosition(index).rotation}, ${
              getTextPosition(index).x
            }, ${getTextPosition(index).y})`
          "
          :fill="prize.color || '#ffffff'"
          :font-size="fontSize"
          font-weight="bold"
          text-anchor="middle"
          dominant-baseline="middle"
          class="segment-label"
        >
          {{ prize.name }}
        </text>
      </g>

      <!-- Center decoration circle -->
      <circle
        :cx="center"
        :cy="center"
        :r="centerRadius + 5"
        fill="url(#goldGradient)"
        filter="url(#glow)"
      />
    </svg>

    <!-- Center spin button -->
    <button
      class="spin-button"
      :class="{ spinning: isSpinning, disabled: disabled || isSpinning }"
      :disabled="disabled || isSpinning"
      :style="buttonStyle"
      @click="handleClick"
      aria-label="Spin the wheel"
    >
      <span class="button-text">{{ buttonText }}</span>
      <span class="button-shine" />
    </button>

    <!-- Pointer / Arrow -->
    <div
      class="wheel-pointer"
      :class="{ bounce: pointerBounce }"
      :style="pointerStyle"
    >
      <svg viewBox="0 0 40 50" width="40" height="50">
        <defs>
          <linearGradient
            id="pointerGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stop-color="#ffd700" />
            <stop offset="50%" stop-color="#d4af37" />
            <stop offset="100%" stop-color="#b8860b" />
          </linearGradient>
        </defs>
        <polygon
          points="20,50 0,0 40,0"
          fill="url(#pointerGradient)"
          stroke="#b8860b"
          stroke-width="2"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted
} from '@vue/composition-api'
import {
  useSpinWheel,
  generateSegmentPath,
  getSegmentTextPosition
} from './useSpinWheel'
import { useWheelAudio, useHaptic } from './useWheelAudio'

export default {
  name: 'SpinWheel',

  props: {
    // Prize configuration
    prizes: {
      type: Array,
      required: true,
      validator: prizes => prizes.length >= 2
    },

    // Visual customization
    size: {
      type: Number,
      default: 300
    },
    borderWidth: {
      type: Number,
      default: 8
    },
    borderColor: {
      type: String,
      default: '#d4af37'
    },
    fontSize: {
      type: Number,
      default: 14
    },
    buttonText: {
      type: String,
      default: 'SPIN'
    },
    pointerPosition: {
      type: String,
      default: 'top',
      validator: v => ['top', 'right'].includes(v)
    },

    // Animation
    duration: {
      type: Number,
      default: 4000
    },
    minSpins: {
      type: Number,
      default: 5
    },
    maxSpins: {
      type: Number,
      default: 8
    },

    // Effects (configurable)
    enableSound: {
      type: Boolean,
      default: true
    },
    enableHaptic: {
      type: Boolean,
      default: true
    },
    enableSparkles: {
      type: Boolean,
      default: true
    },
    soundVolume: {
      type: Number,
      default: 0.5
    },

    // Control
    disabled: {
      type: Boolean,
      default: false
    },
    targetPrizeIndex: {
      type: Number,
      default: null
    },
    // When true, waits for targetPrizeIndex to be set before spinning
    verify: {
      type: Boolean,
      default: false
    }
  },

  emits: ['spin-start', 'spin-end', 'click', 'rotate-start'],

  setup(props, { emit }) {
    // Composables
    const { isSpinning, rotationStyle, spin, reset } = useSpinWheel({
      minSpins: props.minSpins,
      maxSpins: props.maxSpins,
      duration: props.duration
    })

    // Track if we're waiting for verification
    const waitingForVerify = ref(false)

    const audio = useWheelAudio({
      enabled: props.enableSound,
      volume: props.soundVolume
    })

    const haptic = useHaptic({
      enabled: props.enableHaptic
    })

    // Refs
    const wheelSvg = ref(null)
    const pointerBounce = ref(false)
    const lightActive = ref(true)
    const lightPhase = ref(0)
    let lightInterval = null

    // Computed dimensions
    const svgSize = computed(() => props.size)
    const center = computed(() => props.size / 2)
    const radius = computed(() => props.size / 2 - 10)
    const centerRadius = computed(() => props.size * 0.12)

    // Styles
    const containerStyle = computed(() => ({
      '--wheel-size': `${props.size}px`
    }))

    const wheelStyle = computed(() => ({
      ...rotationStyle.value,
      willChange: isSpinning.value ? 'transform' : 'auto'
    }))

    const buttonStyle = computed(() => ({
      width: `${centerRadius.value * 2}px`,
      height: `${centerRadius.value * 2}px`
    }))

    const pointerStyle = computed(() => {
      if (props.pointerPosition === 'right') {
        return {
          top: '50%',
          right: '-25px',
          transform: 'translateY(-50%) rotate(90deg)'
        }
      }
      // Pointer at top, pointing down at the wheel
      return {
        top: '-5px',
        left: '50%',
        transform: 'translateX(-50%)'
      }
    })

    // Methods
    function getSegmentPath(index) {
      return generateSegmentPath(
        index,
        props.prizes.length,
        radius.value,
        centerRadius.value + 10,
        center.value // Pass the correct center
      )
    }

    function getTextPosition(index) {
      return getSegmentTextPosition(
        index,
        props.prizes.length,
        radius.value,
        radius.value * 0.7,
        center.value // Pass the correct center
      )
    }

    function getSparkleStyle(index) {
      const angle = (index / 12) * 360
      const distance = props.size / 2 + 15
      return {
        '--angle': `${angle}deg`,
        '--distance': `${distance}px`,
        '--delay': `${index * 0.1}s`
      }
    }

    function getLightStyle(index, total) {
      const angle = ((index - 1) / total) * 360 - 90
      const rad = (angle * Math.PI) / 180
      const distance = props.size / 2 + 5
      const x = Math.cos(rad) * distance
      const y = Math.sin(rad) * distance
      return {
        transform: `translate(${x}px, ${y}px)`
      }
    }

    function lightenColor(hex, percent) {
      // Parse hex color without bitwise operators
      const cleanHex = hex.replace('#', '')
      const r = parseInt(cleanHex.substring(0, 2), 16)
      const g = parseInt(cleanHex.substring(2, 4), 16)
      const b = parseInt(cleanHex.substring(4, 6), 16)

      const amt = Math.round(2.55 * percent)
      const clamp = v => Math.min(255, Math.max(0, v))

      const toHex = val => {
        const hexStr = clamp(val).toString(16)
        return hexStr.padStart(2, '0')
      }

      return `#${toHex(r + amt)}${toHex(g + amt)}${toHex(b + amt)}`
    }

    // Define doSpin before handleClick (to avoid "used before defined" error)
    async function doSpin() {
      waitingForVerify.value = false
      emit('spin-start')

      const result = await spin({
        prizes: props.prizes,
        targetIndex: props.targetPrizeIndex
      })

      if (result) {
        // Trigger pointer bounce
        pointerBounce.value = true
        setTimeout(() => {
          pointerBounce.value = false
        }, 500)

        // Play win effects
        audio.playWin()
        haptic.trigger('win')

        emit('spin-end', result)
      }
    }

    function cancelVerify() {
      waitingForVerify.value = false
    }

    async function handleClick() {
      if (props.disabled || isSpinning.value || waitingForVerify.value) return

      audio.playClick()
      haptic.trigger('medium')

      if (props.verify) {
        // In verify mode, emit rotate-start and wait for parent to call doSpin
        waitingForVerify.value = true
        emit('rotate-start', doSpin)
      } else {
        // Direct spin without verification
        emit('click')
        await doSpin()
      }
    }

    // Watch for prop changes
    watch(
      () => props.enableSound,
      val => {
        audio.setEnabled(val)
      }
    )

    watch(
      () => props.soundVolume,
      val => {
        audio.setVolume(val)
      }
    )

    watch(
      () => props.enableHaptic,
      val => {
        haptic.setEnabled(val)
      }
    )

    // Light animation
    onMounted(() => {
      lightInterval = setInterval(() => {
        lightPhase.value = lightPhase.value === 0 ? 1 : 0
      }, 500)
    })

    onUnmounted(() => {
      if (lightInterval) {
        clearInterval(lightInterval)
      }
    })

    return {
      // Refs
      wheelSvg,
      isSpinning,
      pointerBounce,
      lightActive,
      lightPhase,

      // Computed
      svgSize,
      center,
      radius,
      centerRadius,
      containerStyle,
      wheelStyle,
      buttonStyle,
      pointerStyle,

      // Methods
      getSegmentPath,
      getTextPosition,
      getSparkleStyle,
      getLightStyle,
      lightenColor,
      handleClick,
      doSpin,
      cancelVerify,
      reset
    }
  }
}
</script>

<style lang="scss" scoped>
.spin-wheel-container {
  --wheel-size: 300px;

  position: relative;
  width: var(--wheel-size);
  height: var(--wheel-size);
  touch-action: none;
  user-select: none;
}

// Sparkles
.sparkles {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
}

.sparkle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: gold;
  border-radius: 50%;
  transform: rotate(var(--angle)) translateY(calc(-1 * var(--distance)));
  animation: sparkle 1.5s ease-in-out infinite;
  animation-delay: var(--delay);
  box-shadow: 0 0 6px gold;
}

@keyframes sparkle {
  0%,
  100% {
    opacity: 0.3;
    transform: rotate(var(--angle)) translateY(calc(-1 * var(--distance)))
      scale(0.5);
  }
  50% {
    opacity: 1;
    transform: rotate(var(--angle)) translateY(calc(-1 * var(--distance)))
      scale(1);
  }
}

// Outer glow ring
.wheel-glow {
  position: absolute;
  top: -15px;
  left: -15px;
  right: -15px;
  bottom: -15px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(212, 175, 55, 0.4) 0%,
    rgba(212, 175, 55, 0.1) 50%,
    transparent 70%
  );
  pointer-events: none;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

// Light bulbs
.wheel-lights {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
}

.light-bulb {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #ffcc00;
  border-radius: 50%;
  border: 1px solid #b8860b;
  box-shadow: 0 0 4px rgba(255, 204, 0, 0.3);
  transition: all 0.3s ease;

  &.active {
    background: #fff;
    box-shadow: 0 0 10px #ffcc00, 0 0 20px rgba(255, 204, 0, 0.5);
  }
}

// SVG Wheel
.wheel-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.segment {
  cursor: default;
}

.segment-label {
  pointer-events: none;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

// Center spin button
.spin-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 4px solid #b8860b;
  background: radial-gradient(circle at 30% 30%, #ff6b6b, #c0392b);
  color: white;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(192, 57, 43, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.3),
    inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 10;

  &:not(.disabled):hover {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: 0 6px 20px rgba(192, 57, 43, 0.5),
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  }

  &:not(.disabled):active {
    transform: translate(-50%, -50%) scale(0.98);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:not(.spinning):not(.disabled) {
    animation: button-pulse 2s ease-in-out infinite;
  }

  &.spinning {
    animation: none;
  }
}

.button-text {
  position: relative;
  z-index: 2;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 40%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 60%
  );
  animation: shine 3s infinite;
}

@keyframes button-pulse {
  0%,
  100% {
    box-shadow: 0 4px 15px rgba(192, 57, 43, 0.4),
      0 0 20px rgba(255, 107, 107, 0.3),
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  }
  50% {
    box-shadow: 0 4px 15px rgba(192, 57, 43, 0.4),
      0 0 40px rgba(255, 107, 107, 0.6),
      inset 0 2px 4px rgba(255, 255, 255, 0.3),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  }
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  30%,
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

// Pointer
.wheel-pointer {
  position: absolute;
  z-index: 20;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);

  &.bounce {
    animation: pointer-bounce 0.5s ease-out;
  }
}

@keyframes pointer-bounce {
  0%,
  100% {
    transform: translateX(-50%) translateY(0);
  }
  30% {
    transform: translateX(-50%) translateY(5px);
  }
  60% {
    transform: translateX(-50%) translateY(2px);
  }
}

// Mobile optimizations
@media (hover: none) {
  .spin-wheel-container {
    -webkit-tap-highlight-color: transparent;
  }

  .spin-button:not(.disabled):hover {
    transform: translate(-50%, -50%);
  }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
  .sparkle,
  .wheel-glow,
  .spin-button,
  .button-shine,
  .wheel-pointer {
    animation: none;
  }

  .light-bulb {
    transition: none;
  }
}
</style>
